<template>
	<ClientOnly>
		<div
			v-if="show"
			id="lipscore-review-list"
			class="lipscore-review-list"
			:data-ls-product-name="productName"
			:data-ls-brand="brand"
			:data-ls-product-id="productId"
			:data-ls-product-url="productUrl"
			:data-ls-sku="sku"
			:data-ls-description="description"
			:data-ls-image-url="imageUrl"
			:data-ls-price="price"
			:data-ls-price-currency="currency"
			:data-ls-availability="availability"
			:data-ls-category="category"
		/>
		<p v-else-if="apiKey && isResolved && !productId" class="text-sm">
			{{ $t("reviews.none_yet") }}
		</p>
	</ClientOnly>
</template>

<script setup lang="ts">
import { resolveLipscoreProductId } from "~/utils/lipscore/resolveLipscoreProductId";
import { scheduleLipscoreReInit } from "~/utils/lipscore/loadLipscoreScript";

const { selectedProduct } = storeToRefs(useProductStore());
const { currentCurrency } = storeToRefs(useNorceStore());
const { getProductStock } = useNorcePwa();
const requestURL = useRequestURL();
const route = useRoute();

const apiKey = useLipscoreApiKey();

const productName = computed(
	() => selectedProduct.value?.VariantName || selectedProduct.value?.Name || ""
);
const brand = computed(() => selectedProduct.value?.Manufacturer?.Name || "");
// [NF-350] Same id rule as LipscoreProductRating.
const { lipscoreIdSource } = useRuntimeConfig().public.norce;
const groupByKey = computed(() => selectedProduct.value?.GroupByKey || "");
const productId = ref("");
const { resolveId } = useLipscoreId();
const productUrl = computed(() => `${requestURL.origin}${route.fullPath}`);
const sku = computed(() => selectedProduct.value?.PartNo || "");
const description = computed(
	() =>
		selectedProduct.value?.SubHeader || selectedProduct.value?.Description || ""
);
const imageUrl = computed(() => selectedProduct.value?.Files?.[0]?.Path || "");
// Gated B2B guests must not get the price in the widget markup.
const { isPriceHidden } = usePriceVisibility();
const price = computed(() =>
	isPriceHidden.value ? undefined : selectedProduct.value?.PriceIncVat || 0
);
const currency = computed(() => currentCurrency.value?.Code || "SEK");
const availability = computed(() => {
	if (!selectedProduct.value) return 0;
	return Math.max(getProductStock(selectedProduct.value), 0);
});
const category = computed(() => {
	const paths = (selectedProduct.value as any)?.CategoryPaths as
		| Array<{ Name?: string }>
		| undefined;
	return (
		paths
			?.map((p) => p.Name)
			.filter(Boolean)
			.join("-") || ""
	);
});

const show = computed(
	() => !!apiKey.value && !!productName.value && !!productId.value
);

// Guards against a slow lookup for a previously selected product landing after
// a newer one and pairing product A's id with product B's name, price and sku.
let latestRequest = 0;

// Lets the tab say "no reviews yet" once the lookup finds no id.
const isResolved = ref(false);

// `force` only on a product change: the list's data changed in place.
const initLipscore = async (force = false) => {
	const request = ++latestRequest;
	productId.value = "";
	isResolved.value = false;
	if (!apiKey.value) return;
	const code = await resolveLipscoreProductId({
		source: lipscoreIdSource,
		partNo: sku.value,
		groupByKey: groupByKey.value,
		resolveProductCode: resolveId
	});
	if (request !== latestRequest) return;
	productId.value = code;
	isResolved.value = true;
	if (!code) return;
	await nextTick();
	scheduleLipscoreReInit({ force });
};

onMounted(() => {
	if (import.meta.client) initLipscore();
});
// The component outlives a product change, so the review list has to follow the
// selection rather than resolving once on mount.
watch(
	() => selectedProduct.value?.PartNo,
	() => {
		if (import.meta.client) initLipscore(true);
	}
);
</script>
